import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import React from "react";
import { type Mode } from "~/features/ui/get-theme-style";
import { Headline } from "~/features/ui/headline";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type ShopHeroBlockViewProps = {
  anchor?: {
    id?: string | null;
    name?: string | null;
  } | null;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  theme?: string | null;
  headline?: string | null;
  text?: React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
};

/**
 * Shop landing hero: large heading on the left, supporting text on the right,
 * split by a vertical divider on desktop. Larger display size than the shared
 * `headline` section. Light/dark follows the section's `data-theme`.
 */
export function ShopHeroBlockView({
  anchor,
  hasTopDivider = true,
  mode = "light",
  theme = "white",
  headline,
  text,
  sectionProps
}: ShopHeroBlockViewProps) {
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={anchor?.id || undefined} data-anchor-name={anchor?.name || undefined} $_css={[{
    "position": "relative",
    "color": "var(--text-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <_StyledDiv $_css2={[{
      "display": "flex",
      "flexDirection": "column",
      "@media (min-width: 1024px)": {
        "flexDirection": "row"
      }
    }]}>
        <_StyledDiv2 $_css3={[{
        "display": "flex",
        "alignItems": "center",
        "paddingLeft": "var(--spacing-lg)",
        "paddingRight": "var(--spacing-lg)",
        "paddingBottom": "var(--spacing-lg)",
        "paddingTop": "var(--spacing-2xl)",
        "@media (min-width: 1024px)": {
          "width": "50%",
          "paddingLeft": "var(--spacing-3xl)",
          "paddingRight": "var(--spacing-3xl)",
          "paddingTop": "var(--spacing-4xl)",
          "paddingBottom": "var(--spacing-4xl)"
        }
      }]}>
          {headline && <_StyledHeadline type="h1" text={headline} />}
        </_StyledDiv2>

        {text && <_StyledDiv3 $_css4={[{
        "paddingLeft": "var(--spacing-lg)",
        "paddingRight": "var(--spacing-lg)",
        "paddingBottom": "var(--spacing-2xl)",
        "@media (min-width: 1024px)": {
          "width": "50%",
          "borderLeftWidth": "1px",
          "borderColor": "var(--stroke-primary)",
          "paddingLeft": "var(--spacing-3xl)",
          "paddingRight": "var(--spacing-3xl)",
          "paddingTop": "var(--spacing-4xl)",
          "paddingBottom": "var(--spacing-4xl)"
        }
      }, {
        "fontSize": "1.6rem",
        "lineHeight": "1.5",
        "fontWeight": "400",
        "color": "var(--text-primary)",
        "@media (min-width: 1024px)": {
          "fontSize": "1.8rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.018rem",
          "fontWeight": "400"
        }
      }]}>
            {text}
          </_StyledDiv3>}
      </_StyledDiv>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-ipdyhy-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-ipdyhy-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-ipdyhy-2"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled4(Headline).withConfig({
  componentId: "sc-sf-ipdyhy-3"
})({
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "5.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.054000000000000006rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "shop-hero___StyledDiv3",
  componentId: "sc-8ybt2h-0"
})(["", ""], p => p.$_css4);